import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  AlertTriangle, 
  FileText, 
  Upload, 
  X, 
  Clock,
  CheckCircle,
  AlertCircle,
  ChevronRight,
  Loader2,
  Image,
  File
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALLOWED_EXTENSIONS = [".pdf", ".jpg", ".jpeg", ".png", ".webp"];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const PendingDocumentsModal = ({ isOpen, onClose }) => {
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingRequestId, setUploadingRequestId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const [selectedRequestId, setSelectedRequestId] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchPendingRequests();
    }
  }, [isOpen]);

  const fetchPendingRequests = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/document-requests/pending`);
      setPendingRequests(res.data);
    } catch (error) {
      console.error('Error fetching pending requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-700 border-red-200';
      case 'urgent': return 'bg-amber-100 text-amber-700 border-amber-200';
      default: return 'bg-blue-100 text-blue-700 border-blue-200';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'critical': return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'urgent': return <Clock className="w-4 h-4 text-amber-500" />;
      default: return <FileText className="w-4 h-4 text-blue-500" />;
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'critical': return 'Crítico';
      case 'urgent': return 'Urgente';
      default: return 'Normal';
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffDays === 0) return 'Hoy';
    if (diffDays === 1) return 'Ayer';
    if (diffDays < 7) return `Hace ${diffDays} días`;
    return date.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
  };

  const handleUploadClick = (requestId) => {
    setSelectedRequestId(requestId);
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedRequestId) return;

    // Validate file
    const ext = `.${file.name.split('.').pop().toLowerCase()}`;
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      toast.error(`Formato no permitido. Use: ${ALLOWED_EXTENSIONS.join(", ")}`);
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error("Archivo muy grande. Máximo: 10MB");
      return;
    }

    // Find the request to get document type
    const request = pendingRequests.find(r => r.id === selectedRequestId);
    if (!request) return;

    setUploadingRequestId(selectedRequestId);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("document_type", request.document_type || "other");
    formData.append("notes", `Respuesta a solicitud: ${request.message}`);

    try {
      // Step 1: Upload the document
      const uploadRes = await axios.post(`${API}/documents/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      // Step 2: Link the document to the request
      await axios.put(`${API}/document-requests/${selectedRequestId}/link-document?document_id=${uploadRes.data.document_id}`);

      toast.success("Documento subido y enviado al oficial");
      fetchPendingRequests();
    } catch (error) {
      console.error('Upload error:', error);
      let errorMessage = "Error al subir documento";
      if (error.code === 'ECONNABORTED') {
        errorMessage = "Tiempo de espera agotado. Intenta de nuevo.";
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      toast.error(errorMessage);
    } finally {
      setUploadingRequestId(null);
      setUploadProgress(0);
      setSelectedRequestId(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept=".pdf,.jpg,.jpeg,.png,.webp,image/*"
            onChange={handleFileSelect}
          />

          {/* Header */}
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Documentos Pendientes</h2>
                  <p className="text-white/80 text-sm">Tu oficial migratorio necesita estos documentos</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : pendingRequests.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-navy-900 mb-2">¡Todo al día!</h3>
                <p className="text-navy-500">No tienes documentos pendientes por entregar.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm text-navy-500 mb-4">
                  <AlertCircle className="w-4 h-4" />
                  <span>Tienes <strong className="text-amber-600">{pendingRequests.length}</strong> documento(s) pendiente(s)</span>
                </div>

                {pendingRequests.map((request) => (
                  <motion.div
                    key={request.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="border border-navy-100 rounded-xl p-4 hover:border-navy-200 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          request.priority === 'critical' ? 'bg-red-100' :
                          request.priority === 'urgent' ? 'bg-amber-100' : 'bg-blue-100'
                        }`}>
                          {getPriorityIcon(request.priority)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-navy-900">{request.document_name}</h4>
                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getPriorityColor(request.priority)}`}>
                              {getPriorityLabel(request.priority)}
                            </span>
                          </div>
                          <p className="text-sm text-navy-600 mb-2">"{request.message}"</p>
                          <div className="flex items-center gap-4 text-xs text-navy-400">
                            <span>Solicitado por: {request.oficial_name}</span>
                            <span>{formatTimeAgo(request.created_at)}</span>
                          </div>
                          {request.status === 'rejected' && request.oficial_notes && (
                            <div className="mt-2 p-2 bg-red-50 border border-red-100 rounded-lg">
                              <p className="text-xs text-red-700">
                                <strong>Corrección necesaria:</strong> {request.oficial_notes}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 flex items-center justify-end gap-2">
                      {uploadingRequestId === request.id ? (
                        <div className="flex items-center gap-3 px-4 py-2 bg-emerald-50 rounded-lg">
                          <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
                          <span className="text-sm text-emerald-700">Subiendo... {uploadProgress}%</span>
                        </div>
                      ) : (
                        <Button
                          onClick={() => handleUploadClick(request.id)}
                          className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          <Upload className="w-4 h-4" />
                          Subir Documento
                        </Button>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-navy-100 bg-navy-50 flex items-center justify-between">
            <p className="text-xs text-navy-500">
              Estos documentos son necesarios para avanzar en tu proceso
            </p>
            <Button
              variant="ghost"
              onClick={onClose}
              className="text-navy-600"
            >
              Ver después
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PendingDocumentsModal;
